/**
 * predictor.mjs — next-word prediction, trained on the module that explains it.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next".
 *
 * ── THE LOAD-BEARING DISTINCTION OF THE WHOLE COURSE ──────────────────────────────────────────
 * §1 says so itself: "Hold this one line: a language model is a fluent imitator of how knowledge
 * sounds, not a source of knowledge. This is the load-bearing distinction of the entire course."
 *
 * And it gives the mechanism in one sentence: "A language model is a system that, given some words,
 * predicts the next word. That is the whole mechanism, all the way down."
 *
 * So the instrument predicts next words, from a table the learner can see, and the table is built
 * from the module's own text. A learner watches a fluent sentence assemble itself out of nothing but
 * counts — and every count is on screen. Reading "it has no idea whether the sentence is true" is a
 * claim; watching the sentence come out of a frequency table is the thing itself.
 *
 * ── WHAT THIS IS NOT, STATED HERE AND ON THE SURFACE ──────────────────────────────────────────
 * This is a BIGRAM table: it looks at one word and counts what followed it. A language model
 * predicts "based on every token that came before it" — the module's words — which is a different
 * and far larger thing. The toy is honest about the shape and dishonest about nothing: same
 * mechanism (count, weigh, pick), incomparable scale and context. Calling it "an LLM in your
 * browser" would be the wrapper marketing the module teaches you to spot, three sections later.
 *
 * ── AND WHAT IT MUST NEVER GENERATE ───────────────────────────────────────────────────────────
 * §1's examples of dangerous fluency are deliberate: "the correct dose adjustment for a specific
 * medicine in pregnancy… the safe interaction between two prescriptions… whether your tax filing
 * should claim a particular deduction… how a clause in a rental contract should be interpreted."
 * A lab that generated confident medical or legal sentences to demonstrate the danger would be
 * producing exactly the artefact the course warns about — and a screenshot of it would carry no
 * label. The corpus is the module's own prose about AI, and nothing else. The gate enforces it.
 */

export const SOURCE_1 = 'CodeAI101 module 01 §1 "What a language model actually is"';
export const SOURCE_3 = 'CodeAI101 module 01 §3 "The wrapper failure pattern — how to spot one"';
export const SOURCE_4 = 'CodeAI101 module 01 §4 "Substrate vs wrapper — what a real AI product is built on"';

export const THE_LINE = 'a language model is a fluent imitator of how knowledge sounds, not a source of knowledge.';
export const THE_MECHANISM = 'A language model is a system that, given some words, predicts the next word. That is the whole mechanism, all the way down.';
export const NO_IDEA = 'The sentence will sound authoritative. The model has no idea whether the sentence is true. It is the shape of a true sentence.';
export const IMITATING = 'When it produces a reply that sounds like reasoning, it is because human writing already encoded reasoning, and the model is imitating the shape of that.';
export const CHECK_ELSEWHERE = 'Truth has to be checked against something else — a textbook, a regulation, a doctor, a lawyer, a real source.';

/** §3's three tells, quoted. The checklist a learner can carry into a sales meeting. */
export const WRAPPER_TELLS = Object.freeze([
  { n: 1, name: 'The same answers regardless of input',
    quote: 'Ask three different domain-specific questions and see if the responses sound generically intelligent but cite nothing specific to your situation.' },
  { n: 2, name: 'No declared data source',
    quote: 'The system cannot tell you where its answer came from. Press for the underlying document, the regulation, the calculation. A real product points at a specific source.' },
  { n: 3, name: 'No verification path',
    quote: 'There is no audit trail, no source citation, no proof that this specific answer corresponds to anything real. The product is one fluent paragraph at a time, and one paragraph cannot be audited.' },
]);
export const CANNOT_SURVIVE = 'Wrappers fail in production for one reason: they cannot survive a real dispute.';
export const NEVER_ADMISSIBLE = 'When real money or a real outcome is on the table, "the AI said so" is never an admissible answer.';

/** Words that carry no meaning as a starting point and make a dull walk. */
const SKIP = new Set(['the', 'a', 'an', 'of', 'to', 'and', 'is', 'it', 'in', 'that', 'this', 'for', 'as', 'on', 'at', 'by', 'or', 'be']);

/**
 * Build the table. One pass over the text: for each word, count what followed it.
 *
 * Deliberately the simplest thing that is still the real mechanism — count, weigh, pick. A learner
 * who can read this paragraph can hold the whole model in their head, which is the point: the
 * mystery is scale, not magic.
 */
export function buildTable(text) {
  const words = String(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[#>|*_`\[\]()]/g, ' ')
    .split(/\s+/)
    .map((w) => w.replace(/^[^A-Za-z0-9'-]+|[^A-Za-z0-9'-]+$/g, ''))
    .filter(Boolean);
  const table = new Map();
  for (let i = 0; i < words.length - 1; i += 1) {
    const k = words[i].toLowerCase();
    if (!table.has(k)) table.set(k, new Map());
    const nxt = table.get(k);
    nxt.set(words[i + 1], (nxt.get(words[i + 1]) || 0) + 1);
  }
  return { table, words };
}

/** What could come next, with counts and shares — the model's entire opinion, visible. */
export function nextWords(table, word, top = 6) {
  const m = table.get(String(word || '').toLowerCase());
  if (!m) return [];
  const total = [...m.values()].reduce((t, n) => t + n, 0);
  return [...m.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, top)
    .map(([w, n]) => ({ word: w, count: n, share: Math.round((100 * n) / total) }));
}

/** Good openings: words the table actually knows, that are worth starting from. */
export function seeds(built, n = 8) {
  const { table } = built;
  return [...table.entries()]
    .filter(([w, m]) => !SKIP.has(w) && w.length > 3 && m.size >= 4)
    .sort((a, b) => b[1].size - a[1].size)
    .slice(0, n)
    .map(([w]) => w);
}

/**
 * Walk the table greedily — always the most likely next word.
 *
 * GREEDY, NOT RANDOM, and that is a decision with a reason: a random walk would give a different
 * sentence each run and the learner could not tell whether the machine or the dice produced it.
 * Greedy is reproducible, so pressing the button twice proves there is no cleverness hiding in the
 * randomness — the same table, the same word, the same answer, every time. Stops when a word has no
 * recorded successor, which is the model running out of table rather than deciding it is finished.
 */
export function walk(built, start, steps = 14) {
  const out = [start];
  const seenPair = new Set();
  let cur = start;
  for (let i = 0; i < steps; i += 1) {
    const opts = nextWords(built.table, cur, 1);
    if (!opts.length) return { words: out, ranOut: true, looped: false };
    const pair = `${cur.toLowerCase()}→${opts[0].word.toLowerCase()}`;
    /* IT LOOPS, AND THE LOOP IS THE HONEST PART. A greedy one-word-context walk falls into "model
       is the model is the model" within a few steps, because the table's whole memory is the word
       it just wrote. Hiding that with a random sampler would make the toy look cleverer than it is;
       naming it teaches the exact thing §1 asserts and this toy cannot show — a real model predicts
       "based on every token that came before it", which is why its output does not fold in half
       after six words. The loop is where the toy ends and the module's sentence begins. */
    if (seenPair.has(pair)) return { words: out, ranOut: false, looped: true };
    seenPair.add(pair);
    cur = opts[0].word;
    out.push(cur);
  }
  return { words: out, ranOut: false, looped: false };
}

/** Why it loops, in terms of the difference the module states. */
export const WHY_IT_LOOPS = 'This table remembers exactly one word — the one it just wrote. A language model predicts "based on every token that came before it", which is why a real one does not fold in half after six words. Same mechanism, incomparable memory.';
export const CONTEXT_QUOTE = 'guessing the next token (a token is roughly a word or part of a word) based on every token that came before it';

/**
 * The question the instrument must always answer the same way, whatever it produced.
 * It is not a joke: it is §1's claim, and the lab prints it beside every generated sentence.
 */
export const IS_IT_TRUE = 'This sentence was assembled by counting which word followed which. Nothing here checked whether it is true, because there is nothing here that could.';
